import { CHART_TYPES, TIMEFRAME_LABELS } from './catalog'
import { describeTalib, resolveDef, summarizeInputs } from '@/indicators'
import {
  collectTradeIdeas,
  ideaDirection,
  ideaRiskReward,
  toTradeMark,
  TRADE_MARK,
  type TradeIdea,
} from './tradeIdeas'
import { ideaName } from './tradeIdeaVocab'
import type { SavedDrawing } from './workbenchState'
import type { ChartTypeId, IndicatorSettings, VolumeState, WorkbenchNote } from './types'

/**
 * Turns a serialized chart environment into the "chart context" text the
 * composer's Send to AI hands the model, plus a map of diffable items so later
 * sends carry only what changed, and a parallel set of person-readable views
 * that list those changes in the chat. Pure module — trivially unit-testable.
 *
 * Item keys are stable across sessions:
 *   market            — symbol · timeframe (candle style is appearance, not
 *                       strategy input — it must never read as a change)
 *   setup:<setupId>   — paired entry+exit trade markers (extendData.setupId)
 *   leg:<ctxId>       — unpaired trade markers
 *   draw:<ctxId>      — geometric drawings (ctxId stamped into extendData)
 *   ind:<name>[#k]    — indicator instances (per-base ordinal: first keeps
 *                       ind:<name>, duplicates get ind:<name>#2, #3, …)
 *   note:<id>         — on-chart notes
 * The visible window and capture time are header text, NOT items — panning
 * or zooming must never read as a chart change.
 */

/** What a drawn element is, for its icon and grouping in the "what changed" list. */
export type WorkbenchChangeKind = 'idea' | 'setup' | 'marker' | 'drawing' | 'note' | 'unknown'

/**
 * The PERSON's view of one context item — the model reads `items[key]`, the user
 * reads this. Formatted at build time so a sent update renders identically from
 * chat history without the chart that produced it.
 */
export interface ItemView {
  kind: WorkbenchChangeKind
  title: string
  fields: Array<{ label: string; value: string }>
}

export type WorkbenchChangeStatus = 'added' | 'edited' | 'removed'

export interface WorkbenchChangeField {
  label: string
  value: string
  /** Edited items only: what the field read before, when it differs. */
  before?: string
}

export interface WorkbenchChange {
  status: WorkbenchChangeStatus
  kind: WorkbenchChangeKind
  title: string
  fields: WorkbenchChangeField[]
}

export interface WorkbenchCounts {
  added: number
  edited: number
  removed: number
}

export interface ChartContext {
  label: string
  text: string
  items: Record<string, string>
  /**
   * Views of the drawn items only (DRAWN_PREFIXES) — the diff base for the next list. Uncapped,
   * so an edit past the wire's field caps still registers; capChange cuts only what is sent.
   * (A diff base saved while views were capped compares as edited once, for an item whose view
   * outgrew the old caps — that one send restates it in full, which the model never had.)
   */
  views: Record<string, ItemView>
  /** The AI has never seen this symbol's chart: the text is a full snapshot. */
  first: boolean
  /** Drawn elements added / edited / removed since the last send — capped at MAX_CHANGES. */
  changes: WorkbenchChange[]
  /** Uncapped tallies of `changes`. */
  counts: WorkbenchCounts
  /** Changes beyond the cap, left out of `changes`. */
  omitted: number
}

/** The chart environment the context is built from — structurally a symbol
 * plus the symbol-bound fields of a persisted slice (SymbolSliceState). */
export interface ChartContextState {
  symbol: string
  timeframe: string
  chartType: ChartTypeId
  indicators: Array<{ name: string; version: number; settings: IndicatorSettings }>
  volume: VolumeState
  notes: WorkbenchNote[]
  drawings: SavedDrawing[]
}

export interface BuildChartContextOpts {
  precision?: number | null
  /** Visible candle window (ms timestamps) at push time. */
  window?: { fromTs: number; toTs: number } | null
  /** aiPush.sentItems from the last commit → incremental mode. */
  prevSentItems?: Record<string, string> | null
  /** aiPush.sentViews from the last commit — lets an edit show before → after. */
  prevSentViews?: Record<string, ItemView> | null
  /** Injectable clock for tests. */
  now?: Date
}

const ENTRY_NAMES = ['buyEntry', 'sellEntry']
const LEG_NAMES = ['buyEntry', 'sellEntry', 'exitPoint', 'levelLine']
/** Everything that is a trade marker rather than geometry — excluded from "Drawings". */
const MARK_NAMES = [...LEG_NAMES, TRADE_MARK]

/**
 * A note is sent whole up to this: room for a written strategy brief, so a chart can carry its own
 * instruction, and still small next to TEXT_CAP. The server's reader of the saved chart clips at the
 * same figure (server-ai WorkbenchState.js NOTE_CAP); keep the two equal.
 */
const NOTE_CAP = 1000
/** The quoted note plus its "@ price (time UTC)" or "pinned to …" tail. */
const NOTE_LINE_CAP = NOTE_CAP + 120
/** One line of chart context: a drawing or a legacy marker. Fits a Fib with its levels and a reason. */
const ITEM_CAP = 600
/**
 * One trade idea (or legacy setup) — a block of level lines. Every price line has to survive: a
 * take profit the model never reads is a take profit the strategy never has. A block over the cap
 * is rebuilt with SHORT_REASON_CAP reasons; only dozens of targets ever reach the hard clip.
 */
const IDEA_CAP = 1600
const SHORT_REASON_CAP = 100
const MAX_DRAW_ITEMS = 30
const TEXT_CAP = 12000

/**
 * Items the user DREW — what the "Chart updated" tab is about. Market,
 * indicators and volume still ride along in the text, but never raise it on
 * their own: a fresh chart names its market before anything is drawn.
 */
export const DRAWN_PREFIXES = ['idea:', 'setup:', 'leg:', 'draw:', 'note:']
export const isDrawnKey = (key: string): boolean => DRAWN_PREFIXES.some((p) => key.startsWith(p))

/** The stored change list must stay well under the server's 60KB row. */
const MAX_CHANGES = 40
/** Bytes for label + text + changes on the wire; the server rejects a row over 60000. */
const UPDATE_BYTE_BUDGET = 50_000
/** Fields per change on the wire — the server's limit (WORKBENCH_LIMITS.fields). */
const MAX_FIELDS = 12
const TITLE_CAP = 150
const FIELD_LABEL_CAP = 40
const FIELD_VALUE_CAP = 200

const pad = (n: number) => String(n).padStart(2, '0')

/** UTC 'YYYY-MM-DD HH:mm' — charts are market data, local timezones would lie. */
function fmtTs(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return 'unknown time'
  const d = new Date(ms)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** 'Sep 3, 2026 10:00 UTC' — the same instant as fmtTs, for people. */
export function fmtFriendly(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return 'unknown time'
  const d = new Date(ms)
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
}

function clip(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s
}

interface Ctx {
  price: (v: number | undefined) => string
}

function ed(d: SavedDrawing): Record<string, unknown> {
  return d.extendData ?? {}
}

function reason(d: SavedDrawing, reasonCap = NOTE_CAP): string {
  const note = ed(d).note
  return typeof note === 'string' && note.trim() ? ` — reason: "${clip(note.trim(), reasonCap)}"` : ''
}

function legLine(d: SavedDrawing, ctx: Ctx, reasonCap = NOTE_CAP): string {
  const p = d.points[0] ?? {}
  const label =
    d.name === 'buyEntry' ? 'Buy entry' : d.name === 'sellEntry' ? 'Sell entry' : d.name === 'exitPoint' ? 'Exit' : String(ed(d).label ?? 'Level')
  return `${label} @ ${ctx.price(p.value)} (${fmtTs(p.timestamp)} UTC)${reason(d, reasonCap)}`
}

/** A block of level lines under IDEA_CAP: full reasons when they fit, short ones when not. */
function fitBlock(build: (reasonCap: number) => string): string {
  const full = build(NOTE_CAP)
  return full.length <= IDEA_CAP ? full : clip(build(SHORT_REASON_CAP), IDEA_CAP)
}

function setupBlock(n: number, entry: SavedDrawing, exit: SavedDrawing | null, ctx: Ctx, reasonCap = NOTE_CAP): string {
  const dir = entry.name === 'sellEntry' ? 'SHORT' : 'LONG'
  const lines = [`${n}. ${dir} setup`, `   - ${legLine(entry, ctx, reasonCap)}`]
  if (exit) {
    lines.push(`   - ${legLine(exit, ctx, reasonCap)}`)
    const ev = entry.points[0]?.value
    const xv = exit.points[0]?.value
    if (typeof ev === 'number' && typeof xv === 'number' && ev !== 0) {
      const delta = xv - ev
      const pct = (delta / ev) * 100
      const sign = delta >= 0 ? '+' : '' // negative renders its own '-'
      lines.push(`   - Entry→exit move: ${sign}${ctx.price(delta)} (${sign}${pct.toFixed(2)}%)`)
    }
  } else {
    lines.push('   - (no exit marked yet)')
  }
  return lines.join('\n')
}

/**
 * A trade idea as the model reads it: direction, every level with its reason,
 * and the risk each target is measured against. One block per idea rather than
 * one per level — a stop only means something next to the entry it protects.
 */
function ideaBlock(n: number, idea: TradeIdea, ctx: Ctx, reasonCap = NOTE_CAP): string {
  const dir = ideaDirection(idea) === 'sell' ? 'SHORT' : 'LONG'
  const lines = [`${n}. ${idea.name} — ${dir}`]
  const rr = ideaRiskReward(idea)
  const pct = (v: number) => {
    const base = idea.entry?.value
    return base ? ` (${((v / base) * 100).toFixed(2)}%)` : ''
  }
  for (const m of idea.marks) {
    const why = m.note.trim() ? ` — reason: "${clip(m.note.trim(), reasonCap)}"` : ''
    let metric = ''
    if (rr && m.role === 'sl') {
      metric = ` — risk ${ctx.price(rr.risk)}${pct(rr.risk)}`
    } else if (rr && m.role === 'tp') {
      const t = rr.targets.find((x) => x.slot === m.slot)
      if (t) metric = ` — reward ${ctx.price(t.reward)}${pct(t.reward)}, R:R ${t.rr.toFixed(2)}`
    }
    lines.push(`   - ${m.label} @ ${ctx.price(m.value)} (${fmtTs(m.timestamp)} UTC)${metric}${why}`)
  }
  if (!idea.entry) lines.push('   - (no entry marked yet)')
  if (!idea.stop) lines.push('   - (no stop loss marked yet)')
  if (idea.takeProfits.length === 0) lines.push('   - (no take profit marked yet)')
  return lines.join('\n')
}

function drawingLine(d: SavedDrawing, ctx: Ctx): string {
  const pts = d.points
  const pt = (i: number) => {
    const p = pts[i] ?? {}
    return `${ctx.price(p.value)} (${fmtTs(p.timestamp)} UTC)`
  }
  switch (d.name) {
    case 'horizontalStraightLine':
      return `Horizontal level @ ${ctx.price(pts[0]?.value)}${reason(d)}`
    case 'verticalStraightLine':
      return `Vertical marker @ ${fmtTs(pts[0]?.timestamp)} UTC${reason(d)}`
    case 'segment':
      return `Trend line: ${pt(0)} → ${pt(1)}${reason(d)}`
    case 'rayLine':
      return `Ray: ${pt(0)} → ${pt(1)} (extends right)${reason(d)}`
    case 'straightLine':
      return `Extended line through ${pt(0)} and ${pt(1)}${reason(d)}`
    case 'parallelStraightLine':
      return `Parallel channel: ${pt(0)} → ${pt(1)}, offset line @ ${ctx.price(pts[2]?.value)}${reason(d)}`
    case 'rectangle':
      return `Rectangle zone: ${pt(0)} → ${pt(1)}${reason(d)}`
    case 'arrow':
      return `Arrow: ${pt(0)} → ${pt(1)}${reason(d)}`
    case 'fibRetracement': {
      const levels = ed(d).levels
      const enabled = Array.isArray(levels)
        ? levels
            .filter((l): l is { value: number; enabled?: boolean } => typeof l === 'object' && l !== null && typeof (l as { value?: unknown }).value === 'number')
            .filter((l) => l.enabled !== false)
            .map((l) => l.value)
        : []
      const levelTxt = enabled.length > 0 ? ` (levels: ${enabled.join(', ')})` : ''
      return `Fib retracement: ${pt(0)} → ${pt(1)}${levelTxt}${reason(d)}`
    }
    default:
      return `${d.name}: ${pts.map((_, i) => pt(i)).join(' → ')}${reason(d)}`
  }
}

// ---- Views: the same items, for the person rather than the model -------------

const field = (label: string, value: string) => ({ label, value })

function noteField(label: string, note: unknown): Array<{ label: string; value: string }> {
  return typeof note === 'string' && note.trim() ? [field(label, note.trim())] : []
}

function pointValue(p: { timestamp?: number; value?: number } | undefined, ctx: Ctx): string {
  return `${ctx.price(p?.value)} · ${fmtFriendly(p?.timestamp)}`
}

function ideaView(idea: TradeIdea, ctx: Ctx): ItemView {
  const dir = ideaDirection(idea) === 'sell' ? 'Short' : 'Long'
  const rr = ideaRiskReward(idea)
  const fields: ItemView['fields'] = []
  for (const m of idea.marks) {
    let metric = ''
    if (rr && m.role === 'sl') {
      metric = ` · risk ${ctx.price(rr.risk)}`
    } else if (rr && m.role === 'tp') {
      const t = rr.targets.find((x) => x.slot === m.slot)
      if (t) metric = ` · R:R ${t.rr.toFixed(2)}`
    }
    fields.push(field(m.label, `${pointValue(m, ctx)}${metric}${m.hidden ? ' (hidden)' : ''}`))
    fields.push(...noteField(`${m.label} note`, m.note))
  }
  if (!idea.entry) fields.push(field('Entry', 'Not marked yet'))
  if (!idea.stop) fields.push(field('Stop loss', 'Not marked yet'))
  if (idea.takeProfits.length === 0) fields.push(field('Take profit', 'Not marked yet'))
  return { kind: 'idea', title: `${idea.name} · ${dir}`, fields }
}

function legName(d: SavedDrawing): string {
  return d.name === 'buyEntry' ? 'Buy entry' : d.name === 'sellEntry' ? 'Sell entry' : d.name === 'exitPoint' ? 'Exit' : String(ed(d).label ?? 'Level')
}

function setupView(entry: SavedDrawing, exit: SavedDrawing | null, ctx: Ctx): ItemView {
  const dir = entry.name === 'sellEntry' ? 'Short' : 'Long'
  const fields: ItemView['fields'] = [field('Entry', pointValue(entry.points[0], ctx)), ...noteField('Entry note', ed(entry).note)]
  if (exit) fields.push(field('Exit', pointValue(exit.points[0], ctx)), ...noteField('Exit note', ed(exit).note))
  else fields.push(field('Exit', 'Not marked yet'))
  return { kind: 'setup', title: `${dir} setup`, fields }
}

function markerView(d: SavedDrawing, ctx: Ctx): ItemView {
  return { kind: 'marker', title: legName(d), fields: [field('Price', pointValue(d.points[0], ctx)), ...noteField('Note', ed(d).note)] }
}

/** 'fibRetracement' → 'Fib retracement' — only for overlays this module has no name for. */
function humanize(name: string): string {
  const words = name.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function drawingView(d: SavedDrawing, ctx: Ctx): ItemView {
  const pts = d.points
  const pt = (i: number) => pointValue(pts[i], ctx)
  const two = (a: string, b: string) => [field(a, pt(0)), field(b, pt(1))]
  let title: string
  let fields: ItemView['fields']
  switch (d.name) {
    case 'horizontalStraightLine':
      title = 'Horizontal level'
      fields = [field('Price', ctx.price(pts[0]?.value))]
      break
    case 'verticalStraightLine':
      title = 'Vertical marker'
      fields = [field('Time', fmtFriendly(pts[0]?.timestamp))]
      break
    case 'segment':
      title = 'Trend line'
      fields = two('Start', 'End')
      break
    case 'rayLine':
      title = 'Ray'
      fields = two('Start', 'Through')
      break
    case 'straightLine':
      title = 'Extended line'
      fields = two('Point 1', 'Point 2')
      break
    case 'parallelStraightLine':
      title = 'Parallel channel'
      fields = [...two('Start', 'End'), field('Offset line', ctx.price(pts[2]?.value))]
      break
    case 'rectangle':
      title = 'Rectangle zone'
      fields = two('From', 'To')
      break
    case 'arrow':
      title = 'Arrow'
      fields = two('From', 'To')
      break
    case 'fibRetracement': {
      title = 'Fibonacci retracement'
      const levels = ed(d).levels
      const enabled = Array.isArray(levels)
        ? levels
            .filter((l): l is { value: number; enabled?: boolean } => typeof l === 'object' && l !== null && typeof (l as { value?: unknown }).value === 'number')
            .filter((l) => l.enabled !== false)
            .map((l) => l.value)
        : []
      fields = [...two('From', 'To'), ...(enabled.length > 0 ? [field('Levels', enabled.join(', '))] : [])]
      break
    }
    default:
      title = humanize(d.name)
      fields = pts.map((_, i) => field(`Point ${i + 1}`, pt(i)))
  }
  return { kind: 'drawing', title, fields: [...fields, ...noteField('Note', ed(d).note)] }
}

/** `pinnedTo`: a pinned callout's "Pinned to" reading, in place of where its bubble sits. */
function noteView(n: WorkbenchNote, ctx: Ctx, pinnedTo: string | null = null): ItemView {
  return {
    kind: 'note',
    title: 'Note',
    fields: [
      field('Text', n.text.trim()),
      pinnedTo ? field('Pinned to', pinnedTo) : field('Position', pointValue(n.pos, ctx)),
    ],
  }
}

/**
 * What a pinned callout is pinned to, for the model and for the person — or null for an
 * unpinned one (a callout saved before pins, or a text note).
 *
 * Worded from the frozen label and the pin point, not the drawing's live reading: moving the
 * drawing must not also read as the callout changing.
 */
function calloutPin(
  n: WorkbenchNote,
  drawable: SavedDrawing[],
  ideas: TradeIdea[],
  ctx: Ctx,
): { model: string; human: string } | null {
  const pin = n.kind === 'callout' ? n.pin : undefined
  if (!pin) return null
  const where = `${ctx.price(pin.at.value)} (${fmtTs(pin.at.timestamp)} UTC)`
  const label = n.pinLabel?.trim() || 'a drawing'
  switch (pin.kind) {
    case 'drawing': {
      const here = drawable.some((d) => ed(d).ctxId === pin.ctxId)
      return {
        model: `pinned to the drawing "${label}"${here ? '' : ' (no longer on the chart)'} at ${where}`,
        human: here ? label : `${label} (removed)`,
      }
    }
    case 'ideaLevel': {
      const here = ideas.some((i) => i.ideaId === pin.ideaId && i.marks.some((m) => m.slot === pin.slot))
      return {
        model: `pinned to ${label}${here ? '' : ' (no longer on the chart)'} at ${where}`,
        human: here ? label : `${label} (removed)`,
      }
    }
    case 'candle':
      return {
        model: `pinned to the ${fmtTs(pin.at.timestamp)} UTC candle at ${ctx.price(pin.at.value)}`,
        human: `${fmtFriendly(pin.at.timestamp)} candle`,
      }
    case 'point':
      return { model: `at ${where}`, human: pointValue(pin.at, ctx) }
  }
}

/** A removed item whose view predates this module (a legacy diff base): its text is all there is. */
function fallbackView(key: string, text: string): ItemView {
  const kind: WorkbenchChangeKind = key.startsWith('idea:')
    ? 'idea'
    : key.startsWith('setup:')
      ? 'setup'
      : key.startsWith('leg:')
        ? 'marker'
        : key.startsWith('draw:')
          ? 'drawing'
          : key.startsWith('note:')
            ? 'note'
            : 'unknown'
  // Idea/setup blocks open with their list ordinal ("1. Trade idea #2 — LONG").
  const first = (text.split('\n')[0] ?? '').replace(/^\d+\.\s*/, '')
  return { kind, title: first || 'Chart item', fields: [] }
}

/**
 * The drawn elements that differ from the last send, for the person to read.
 * Never sent (prev = null) lists every drawn item as added. An edit pairs its
 * fields with the previous view's by label; one whose text changed but whose
 * view did not (an idea renumbered because another was removed) is left out —
 * there is nothing a person would recognise as a change.
 */
export function diffDrawnItems(
  items: Record<string, string>,
  views: Record<string, ItemView>,
  prevItems: Record<string, string> | null,
  prevViews: Record<string, ItemView> | null,
): WorkbenchChange[] {
  const changes: WorkbenchChange[] = []
  for (const [key, text] of Object.entries(items)) {
    if (!isDrawnKey(key)) continue
    const view = views[key] ?? fallbackView(key, text)
    if (!prevItems || !(key in prevItems)) {
      changes.push({ status: 'added', kind: view.kind, title: view.title, fields: view.fields.map((f) => ({ ...f })) })
      continue
    }
    if (prevItems[key] === text) continue
    const before = prevViews?.[key]
    if (!before) {
      changes.push({ status: 'edited', kind: view.kind, title: view.title, fields: view.fields.map((f) => ({ ...f })) })
      continue
    }
    const prevByLabel = new Map(before.fields.map((f) => [f.label, f.value]))
    const fields: WorkbenchChangeField[] = view.fields.map((f) => {
      const was = prevByLabel.get(f.label)
      prevByLabel.delete(f.label)
      if (was === f.value) return { ...f }
      return { ...f, before: was ?? '—' }
    })
    // Fields the item no longer has (a note cleared, a target deleted).
    for (const [label, was] of prevByLabel) fields.push({ label, value: '—', before: was })
    const titleChanged = before.title !== view.title
    if (!titleChanged && !fields.some((f) => f.before !== undefined)) continue
    changes.push({ status: 'edited', kind: view.kind, title: view.title, fields })
  }
  if (prevItems) {
    for (const [key, text] of Object.entries(prevItems)) {
      if (!isDrawnKey(key) || key in items) continue
      const view = prevViews?.[key] ?? fallbackView(key, text)
      changes.push({ status: 'removed', kind: view.kind, title: view.title, fields: view.fields.map((f) => ({ ...f })) })
    }
  }
  return changes
}

const utf8 = new TextEncoder()

/**
 * The per-item caps bound each change, not the list: forty edited ideas with long
 * notes would still overflow the stored row. Drop changes from the end until the
 * update fits, and let `omitted` say so — the text the model reads is untouched.
 */
function fitChanges(label: string, text: string, changes: WorkbenchChange[]): WorkbenchChange[] {
  let bytes = utf8.encode(JSON.stringify({ label, text })).length
  const fitted: WorkbenchChange[] = []
  for (const c of changes) {
    const size = utf8.encode(JSON.stringify(c)).length + 1
    if (bytes + size > UPDATE_BYTE_BUDGET) break
    bytes += size
    fitted.push(c)
  }
  return fitted
}

function countChanges(changes: WorkbenchChange[]): WorkbenchCounts {
  const counts: WorkbenchCounts = { added: 0, edited: 0, removed: 0 }
  for (const c of changes) counts[c.status] += 1
  return counts
}

/**
 * The fields a change keeps on the wire: all of them up to MAX_FIELDS, and past that every field
 * that changed (in order) before any that did not — the cap must never hide the one edit an item had.
 */
function pickFields(fields: WorkbenchChangeField[]): WorkbenchChangeField[] {
  if (fields.length <= MAX_FIELDS) return fields
  let room = Math.max(0, MAX_FIELDS - fields.filter((f) => f.before !== undefined).length)
  return fields.filter((f) => f.before !== undefined || room-- > 0).slice(0, MAX_FIELDS)
}

/** Caps a change for the wire. Views stay uncapped (they are the diff base); only this is cut. */
function capChange(c: WorkbenchChange): WorkbenchChange {
  return {
    status: c.status,
    kind: c.kind,
    title: clip(c.title, TITLE_CAP),
    fields: pickFields(c.fields).map((f) => ({
      label: clip(f.label, FIELD_LABEL_CAP),
      value: clip(f.value, FIELD_VALUE_CAP),
      ...(f.before !== undefined ? { before: clip(f.before, FIELD_VALUE_CAP) } : {}),
    })),
  }
}

// ---- Pin titles (what a callout says it is pinned to) ----------------------

/** The name a person knows a drawing or legacy marker by ("Trend line", "Buy entry"). */
export function drawingTitle(d: SavedDrawing): string {
  if (LEG_NAMES.includes(d.name)) return legName(d)
  return drawingView(d, { price: () => '' }).title
}

/** "Trade idea #1 · Stop loss" for one trade-idea level, or null if it is not one. */
export function tradeLevelTitle(d: SavedDrawing): string | null {
  const m = toTradeMark('level', d.name, d.extendData, d.points[0])
  return m ? `${ideaName(m.ideaSeq)} · ${m.label}` : null
}

interface CollectedItems {
  items: Record<string, string>
  views: Record<string, ItemView>
  ideaTexts: string[]
  setupTexts: string[]
  legTexts: string[]
  indicatorTexts: string[]
  drawingTexts: string[]
  noteTexts: string[]
  droppedDrawings: number
}

function collect(state: ChartContextState, ctx: Ctx): CollectedItems {
  const items: Record<string, string> = {}
  const views: Record<string, ItemView> = {}
  const tf = TIMEFRAME_LABELS[state.timeframe as keyof typeof TIMEFRAME_LABELS] ?? state.timeframe
  items.market = `${state.symbol} · ${tf}`

  // Trade markers, excluding connector chrome.
  const drawable = state.drawings.filter((d) => !d.lock && ed(d).connector !== true)

  // Trade ideas first — the current tool. Hidden marks are included: hiding one
  // is a way of decluttering the chart, not a statement that it left the plan.
  const ideaMarks = drawable
    .map((d, i) => toTradeMark(String(i), d.name, d.extendData, d.points[0]))
    .filter((m): m is NonNullable<typeof m> => m != null)
  const ideas = collectTradeIdeas(ideaMarks)
  const ideaTexts = ideas.map((idea, i) => {
    const text = fitBlock((reasonCap) => ideaBlock(i + 1, idea, ctx, reasonCap))
    items[`idea:${idea.ideaId}`] = text
    views[`idea:${idea.ideaId}`] = ideaView(idea, ctx)
    return text
  })

  // LEGACY: the buy/sell entry→exit tool no longer creates these, but saved
  // charts still carry them and the model must still read them.
  const legs = drawable.filter((d) => LEG_NAMES.includes(d.name))
  const entries = legs.filter((d) => ENTRY_NAMES.includes(d.name))
  const exits = legs.filter((d) => d.name === 'exitPoint')
  const otherLegs = legs.filter((d) => !ENTRY_NAMES.includes(d.name) && d.name !== 'exitPoint')

  // Pair by setupId; legacy markers fall back to chronological entry→next-exit.
  const claimed = new Set<SavedDrawing>()
  const setups: Array<{ key: string; entry: SavedDrawing; exit: SavedDrawing | null }> = []
  for (const entry of [...entries].sort((a, b) => (a.points[0]?.timestamp ?? 0) - (b.points[0]?.timestamp ?? 0))) {
    const sid = ed(entry).setupId
    let exit: SavedDrawing | null = null
    if (typeof sid === 'string') {
      exit = exits.find((x) => ed(x).setupId === sid && !claimed.has(x)) ?? null
    }
    if (!exit) {
      const entryTs = entry.points[0]?.timestamp ?? 0
      exit =
        exits
          .filter((x) => !claimed.has(x) && typeof ed(x).setupId !== 'string' && (x.points[0]?.timestamp ?? 0) >= entryTs)
          .sort((a, b) => (a.points[0]?.timestamp ?? 0) - (b.points[0]?.timestamp ?? 0))[0] ?? null
    }
    if (exit) claimed.add(exit)
    claimed.add(entry)
    const key = typeof sid === 'string' ? `setup:${sid}` : `setup:${entry.points[0]?.timestamp ?? 0}:${entry.points[0]?.value ?? 0}`
    setups.push({ key, entry, exit })
  }

  const setupTexts = setups.map((s, i) => {
    const text = fitBlock((reasonCap) => setupBlock(i + 1, s.entry, s.exit, ctx, reasonCap))
    items[s.key] = text
    views[s.key] = setupView(s.entry, s.exit, ctx)
    return text
  })

  const legKey = (d: SavedDrawing) => {
    const cid = ed(d).ctxId
    return typeof cid === 'string' ? `leg:${cid}` : `leg:${d.name}:${d.points[0]?.timestamp ?? 0}:${d.points[0]?.value ?? 0}`
  }
  const legTexts = [...exits.filter((x) => !claimed.has(x)), ...otherLegs].map((d) => {
    const text = clip(legLine(d, ctx), ITEM_CAP)
    items[legKey(d)] = text
    views[legKey(d)] = markerView(d, ctx)
    return text
  })

  // Per-base ordinal keys: the first instance keeps `ind:<name>` (legacy
  // sentItems keys stay valid), duplicates get `ind:<name>#2`, `#3`, … in
  // array order — the same order restore mints engine names in.
  const indOrdinals: Record<string, number> = {}
  const indicatorTexts = state.indicators.map((i) => {
    const k = (indOrdinals[i.name] = (indOrdinals[i.name] ?? 0) + 1)
    const key = k === 1 ? `ind:${i.name}` : `ind:${i.name}#${k}`
    const def = resolveDef(i.name, i.version)
    const pane = def?.pane === 'main' ? 'overlaid on price' : 'own pane'
    const label = def?.shortLabel ?? i.name
    const summary = def ? summarizeInputs(def, i.settings.inputs) : ''
    // The TA-Lib call rides along so generated code computes exactly what the
    // chart shows; a builtin v1 (klinecharts maths) has no such equivalent.
    const text = !def
      ? `${i.name}, ${pane}`
      : def.builtin
        ? `${label} v1 (legacy chart maths${summary ? `, params ${summary}` : ''}; no TA-Lib equivalent), ${pane}`
        : `${label}(${summary})${def.talib ? ` — TA-Lib: ${describeTalib(def, i.settings.inputs)}` : ''}, ${pane}`
    items[key] = text
    return text
  })
  if (state.volume.present) {
    items['ind:Volume'] = 'Volume, overlaid on price'
    indicatorTexts.push('Volume, overlaid on price')
  }

  const geo = drawable.filter((d) => !MARK_NAMES.includes(d.name))
  const droppedDrawings = Math.max(0, geo.length - MAX_DRAW_ITEMS)
  const drawingTexts = geo.slice(0, MAX_DRAW_ITEMS).map((d) => {
    const cid = ed(d).ctxId
    const key = typeof cid === 'string' ? `draw:${cid}` : `draw:${d.name}:${d.points[0]?.timestamp ?? 0}:${d.points[0]?.value ?? 0}`
    const text = clip(drawingLine(d, ctx), ITEM_CAP)
    items[key] = text
    views[key] = drawingView(d, ctx)
    return text
  })

  const noteTexts = state.notes
    .filter((n) => n.text.trim())
    .map((n) => {
      const said = `"${clip(n.text.trim(), NOTE_CAP)}"`
      const pinned = calloutPin(n, drawable, ideas, ctx)
      const text = clip(
        pinned ? `${said} — ${pinned.model}` : `${said} @ ${ctx.price(n.pos.value)} (${fmtTs(n.pos.timestamp)} UTC)`,
        NOTE_LINE_CAP,
      )
      items[`note:${n.id}`] = text
      views[`note:${n.id}`] = noteView(n, ctx, pinned?.human ?? null)
      return text
    })

  return { items, views, ideaTexts, setupTexts, legTexts, indicatorTexts, drawingTexts, noteTexts, droppedDrawings }
}

function section(title: string, lines: string[]): string[] {
  if (lines.length === 0) return []
  return ['', `${title}:`, ...lines.map((l) => (l.includes('\n') ? l : `- ${l}`))]
}

export function buildChartContext(state: ChartContextState, opts: BuildChartContextOpts = {}): ChartContext {
  const precision = opts.precision ?? 5
  const ctx: Ctx = {
    price: (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(precision) : '?'),
  }
  const now = opts.now ?? new Date()
  const tf = TIMEFRAME_LABELS[state.timeframe as keyof typeof TIMEFRAME_LABELS] ?? state.timeframe
  const chartTypeLabel = CHART_TYPES.find((t) => t.id === state.chartType)?.label.toLowerCase() ?? state.chartType
  const label = `${state.symbol} · ${tf} chart`
  const windowLine = opts.window ? `Visible window: ${fmtTs(opts.window.fromTs)} → ${fmtTs(opts.window.toTs)} UTC` : null

  const { items, views, ideaTexts, setupTexts, legTexts, indicatorTexts, drawingTexts, noteTexts, droppedDrawings } =
    collect(state, ctx)

  const prev = opts.prevSentItems ?? null
  let body: string[]

  if (!prev) {
    body = [
      `[CHART] ${state.symbol} · ${tf} ${chartTypeLabel} — shared ${fmtTs(now.getTime())} UTC`,
      ...(windowLine ? [windowLine] : []),
      ...(ideaTexts.length > 0 ? ['', 'Trade ideas:', ...ideaTexts] : []),
      ...(setupTexts.length > 0 ? ['', 'Trade setups:', ...setupTexts] : []),
      ...section('Unpaired trade markers', legTexts),
      ...section('Indicators', indicatorTexts),
      ...section('Drawings', [...drawingTexts, ...(droppedDrawings > 0 ? [`…and ${droppedDrawings} more drawings`] : [])]),
      ...section('Notes', noteTexts),
    ]
  } else {
    const added: string[] = []
    const changed: string[] = []
    const removed: string[] = []
    let tradeTouched = false
    for (const [key, text] of Object.entries(items)) {
      const isTrade = key.startsWith('idea:') || key.startsWith('setup:') || key.startsWith('leg:')
      if (!(key in prev)) {
        added.push(text)
        if (isTrade) tradeTouched = true
      } else if (prev[key] !== text) {
        changed.push(text)
        if (isTrade) tradeTouched = true
      }
    }
    for (const [key, text] of Object.entries(prev)) {
      if (!(key in items)) {
        removed.push(text.split('\n')[0])
        if (key.startsWith('idea:') || key.startsWith('setup:') || key.startsWith('leg:')) tradeTouched = true
      }
    }

    const header = `[CHART UPDATE] ${state.symbol} · ${tf} ${chartTypeLabel} — changes since the last shared chart (${fmtTs(now.getTime())} UTC)`
    if (added.length === 0 && changed.length === 0 && removed.length === 0) {
      body = [header.replace('— changes since', '— no drawing/indicator changes since'), ...(windowLine ? [windowLine] : [])]
    } else {
      body = [
        header,
        ...section('Added', added),
        ...section('Changed', changed),
        ...section('Removed', removed),
        // Trade ideas and setups are the payload the AI reasons about —
        // restate them whole whenever any level moved, so the model never has
        // to patch one together from fragments.
        ...(tradeTouched && ideaTexts.length + setupTexts.length > 0
          ? ['', 'Current trade ideas (in full):', ...ideaTexts, ...setupTexts]
          : []),
        ...(windowLine ? ['', windowLine] : []),
      ]
    }
  }

  let text = body.join('\n')
  if (text.length > TEXT_CAP) text = `${text.slice(0, TEXT_CAP - 12)}\n[truncated]`

  const allChanges = diffDrawnItems(items, views, prev, opts.prevSentViews ?? null)
  const changes = fitChanges(label, text, allChanges.slice(0, MAX_CHANGES).map(capChange))
  return {
    label,
    text,
    items,
    views,
    first: !prev,
    changes,
    counts: countChanges(allChanges),
    omitted: allChanges.length - changes.length,
  }
}
